import { Toggle, ToggleGroup } from '@base-ui/react'
import { motion } from 'framer-motion'
import type { ParseKeys } from 'i18next'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { TAB_MARKER_TRANSITION } from '@/lib/motion'

/* An option is labelled by a translation key or by a literal, the same dual
 * form TabNav accepts and for the same reason: the range control mixes both by
 * nature. "1M" and "3M" are notation that reads identically in every language
 * and should NOT go through the catalogue, while "All" is a word and must. */
export type SegmentedOption<T extends string> = { value: T } & (
  | { label: string; labelKey?: never }
  | { labelKey: ParseKeys; label?: never }
)

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Names the control for assistive tech, e.g. "Turnover date range". */
  label: string
  size?: 'sm' | 'md'
  className?: string
}

/** The shared range control. EXPERIENCE.md: the date range is shared across
 *  Finance views, not reinvented per view (FR-53). */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'sm',
  className,
}: SegmentedControlProps<T>) {
  const { t } = useTranslation()
  // Scoped per instance: Finance and Profile can both render a control on the
  // same screen, and a shared literal id would animate one thumb between them.
  const thumbId = useId()

  return (
    /* ToggleGroup renders the same `role="group"` + `aria-pressed` semantics
       this control already emitted by hand, and adds the roving tabindex the
       hand-rolled version never had: the group is one tab stop and ←/→ move
       between segments, instead of every segment being its own stop. */
    <ToggleGroup
      aria-label={label}
      value={[value]}
      onValueChange={(next) => {
        // An empty array means the pressed segment was pressed again. A
        // segmented control has no "nothing chosen" state, so that is a no-op
        // rather than a deselection.
        const [chosen] = next
        if (chosen !== undefined) onChange(chosen as T)
      }}
      className={cn(
        'border-neutral-100 flex gap-0.5 rounded-md border p-[3px]',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <Toggle
            key={option.value}
            value={option.value}
            className={cn(
              // whitespace-nowrap: a segment is a LABEL, and one that wraps to
              // two lines makes the control taller than the row it sits in and
              // its neighbours ragged. "Last 30 days" is the shortest name that
              // period has; if the control does not fit, the row wraps.
              'relative flex-1 cursor-pointer rounded-[5px] border-0 bg-transparent',
              'whitespace-nowrap',
              'font-sans transition-colors duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              size === 'sm' ? 'h-6 px-2 text-[11.5px]' : 'h-7 px-2.5 text-[12.5px]',
              selected
                ? 'text-text-heading font-medium'
                : 'text-text-muted hover:bg-neutral-50 font-normal',
            )}
          >
            {/* The thumb slides between options instead of the fill jumping.
                It is a sibling behind the label rather than the button's own
                background, which is what lets one element travel the width of
                the control. */}
            {selected ? (
              <motion.span
                layoutId={thumbId}
                transition={TAB_MARKER_TRANSITION}
                aria-hidden
                className="bg-neutral-100 absolute inset-0 rounded-[5px]"
              />
            ) : null}
            <span className="relative">
              {option.labelKey ? t(option.labelKey) : option.label}
            </span>
          </Toggle>
        )
      })}
    </ToggleGroup>
  )
}
